import { useState } from "react";
import { Box, Divider, Flex, Link, useBreakpointValue } from "@chakra-ui/react";
import { Headphones, Mail, MessageCircleQuestion, Phone, Send } from "lucide-react";
import {
  Button,
  InputTextArea,
  Modal,
  ModalBody,
  ModalHeader,
  useToaster,
} from "pixelize-design-library";
import { describeError } from "@core/api";
import { selectUser, useSession } from "@core/auth";
import { usePublicSettings } from "@modules/shared";
import { useSubmitFeedback } from "@modules/feedback";
import { brandColors, radii } from "@core/theme";
import { useNavigate } from "react-router";

const FEEDBACK_MAX_LENGTH = 2000;

const WhatsAppIcon = ({ size, color }: { size: number; color: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color} aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
  </svg>
);

type ContactRowProps = {
  href: string;
  icon: React.ReactNode;
  iconBg: string;
  label: string;
  value: string;
};

const ContactRow = ({ href, icon, iconBg, label, value }: ContactRowProps) => (
  <Flex
    as={Link}
    href={href}
    isExternal={href.startsWith("https://")}
    align="center"
    gap={{ base: 2, md: 3 }}
    p={{ base: 2, md: 3 }}
    borderWidth={1}
    borderColor={brandColors.border[300]}
    borderRadius={radii.base}
    transition="transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease"
    _hover={{
      textDecoration: "none",
      borderColor: brandColors.primary[300],
      boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
      transform: "translateY(-1px)",
    }}
  >
    <Flex
      align="center"
      justify="center"
      boxSize={{ base: "28px", md: "36px" }}
      borderRadius={radii.full}
      bg={iconBg}
      flexShrink={0}
    >
      {icon}
    </Flex>
    <Box>
      <Box fontSize="xs" color={brandColors.gray[500]}>
        {label}
      </Box>
      <Box fontSize={{ base: "xs", md: "sm" }} fontWeight="semibold" color={brandColors.gray[900]}>
        {value}
      </Box>
    </Box>
  </Flex>
);

export const SupportButton = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState("");
  const settingsQuery = usePublicSettings();
  const store = settingsQuery.data?.store;
  const user = useSession(selectUser);
  const navigate = useNavigate();
  const { showToast } = useToaster();
  const submitFeedback = useSubmitFeedback();

  const iconSize = useBreakpointValue({ base: 10, md: 12 }) ?? 10;
  const modalSize = useBreakpointValue<"xs" | "sm">({ base: "xs", md: "sm" }) ?? "xs";

  const close = () => setIsOpen(false);

  const handleSubmitFeedback = () => {
    const trimmed = message.trim();
    if (!trimmed) return;
    submitFeedback.mutate(
      { message: trimmed },
      {
        onSuccess: () => {
          setMessage("");
          showToast({ status: "success", title: "Thanks for the feedback!" });
        },
        onError: (error) => {
          showToast({
            status: "error",
            title: "Could not send feedback",
            description: describeError(error),
          });
        },
      },
    );
  };

  return (
    <>
      <Box
        as="button"
        type="button"
        aria-label="Contact support"
        onClick={() => setIsOpen(true)}
        position="fixed"
        top="50%"
        right={0}
        zIndex={20}
        bg={brandColors.primary[500]}
        color="white"
        px={{ base: 1.5, md: 2 }}
        py={{ base: 2, md: 3 }}
        borderLeftRadius={radii.md}
        boxShadow="-2px 4px 12px rgba(0,0,0,0.25)"
        style={{ transform: "translateY(-50%)" }}
        transition="background-color 0.15s ease, transform 0.15s ease"
        _hover={{ bg: brandColors.primary[600] }}
      >
        <Flex direction="column" align="center" gap={{ base: 1, md: 1.5 }}>
          <Flex
            align="center"
            justify="center"
            boxSize={{ base: "16px", md: "22px" }}
            borderRadius={radii.full}
            bg="whiteAlpha.300"
          >
            <Headphones size={iconSize} color="white" />
          </Flex>
          <Box
            fontWeight="600"
            fontSize={{ base: "10px", md: "xs" }}
            letterSpacing="0.06em"
            style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
          >
            Support
          </Box>
        </Flex>
      </Box>

      <Modal
        isOpen={isOpen}
        onClose={close}
        size={modalSize}
        overlaybg="blackAlpha.600"
        overlaybackdropFilter="blur(0px)"
      >
        <ModalHeader>
          <Flex direction="column" align="center" gap={{ base: 1.5, md: 2 }}>
            <Flex
              align="center"
              justify="center"
              boxSize={{ base: "44px", md: "60px" }}
              borderRadius={radii.full}
              bgGradient={`linear(135deg, ${brandColors.primary[400]}, ${brandColors.primary[700]})`}
              boxShadow={`0 6px 16px ${brandColors.primary[500]}55`}
            >
              <MessageCircleQuestion size={22} color="white" />
            </Flex>
            <Box
              textAlign="center"
              fontSize={{ base: "md", md: "lg" }}
              fontWeight="700"
              color={brandColors.gray[900]}
            >
              Need help?
            </Box>
          </Flex>
        </ModalHeader>
        <ModalBody>
          <Box
            textAlign="center"
            fontSize={{ base: "xs", md: "sm" }}
            color={brandColors.gray[500]}
            mb={{ base: 3, md: 4 }}
          >
            Reach out to {store?.name ?? "our team"} and we&apos;ll get back to you.
          </Box>

          {store !== undefined && (
            <Flex direction="column" gap={{ base: 2, md: 3 }}>
              <ContactRow
                href={`mailto:${store.supportEmail}`}
                icon={<Mail size={14} color={brandColors.primary[600]} />}
                iconBg={brandColors.primary[50]}
                label="Email us"
                value={store.supportEmail}
              />
              {store.supportPhone !== null && (
                <ContactRow
                  href={`tel:${store.supportPhone}`}
                  icon={<Phone size={14} color={brandColors.primary[600]} />}
                  iconBg={brandColors.primary[50]}
                  label="Call us"
                  value={store.supportPhone}
                />
              )}
              {store.supportWhatsapp != null && (
                <ContactRow
                  href={`https://wa.me/${store.supportWhatsapp.replace(/[^\d]/g, "")}`}
                  icon={<WhatsAppIcon size={14} color={brandColors.success[600]} />}
                  iconBg={brandColors.success[50]}
                  label="WhatsApp us"
                  value={store.supportWhatsapp}
                />
              )}
            </Flex>
          )}

          <Divider my={{ base: 4, md: 5 }} borderColor={brandColors.border[300]} />

          <Box
            fontSize={{ base: "xs", md: "sm" }}
            fontWeight="semibold"
            color={brandColors.gray[900]}
            mb={2}
          >
            Send us feedback
          </Box>

          {user === null ? (
            <Flex
              direction="column"
              align="center"
              gap={2}
              p={{ base: 3, md: 4 }}
              borderWidth={1}
              borderStyle="dashed"
              borderColor={brandColors.border[400]}
              borderRadius={radii.base}
            >
              <Box fontSize="xs" color={brandColors.gray[500]} textAlign="center">
                Log in to send us your feedback.
              </Box>
              <Button
                label="Log in"
                size="sm"
                colorScheme="primary"
                onClick={() => {
                  close();
                  void navigate("/login");
                }}
              />
            </Flex>
          ) : (
            <Flex direction="column" gap={2}>
              <InputTextArea
                placeholder="Tell us what's on your mind..."
                value={message}
                onChange={(e) => setMessage(e.target.value.slice(0, FEEDBACK_MAX_LENGTH))}
                rows={3}
                resize="vertical"
                width="100%"
              />
              <Flex justify="flex-end">
                <Button
                  label="Send feedback"
                  size="sm"
                  colorScheme="primary"
                  leftIcon={<Send size={14} />}
                  isDisabled={message.trim().length === 0}
                  isLoading={submitFeedback.isPending}
                  onClick={handleSubmitFeedback}
                />
              </Flex>
            </Flex>
          )}
        </ModalBody>
      </Modal>
    </>
  );
};
